// Phase 9 /analyse/golden-runs — list goldens + promote-a-run flow.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trophy, X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { goldenRuns as goldenApi, runs as runsApi } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

export default function GoldenRunsPage() {
    const [rows, setRows] = useState([]);
    const [allRuns, setAllRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [showPromote, setShowPromote] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const [note, setNote] = useState("");

    const refresh = async () => {
        try {
            setLoading(true);
            const data = await goldenApi.list();
            setRows(Array.isArray(data) ? data : data?.items || []);
            setErr("");
        } catch (e) {
            setErr(formatApiError(e, "Failed to load goldens"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const openPromote = async () => {
        setShowPromote(true);
        if (allRuns.length === 0) {
            try {
                const data = await runsApi.list();
                setAllRuns(Array.isArray(data) ? data : data?.items || []);
            } catch (_e) {
                /* noop */
            }
        }
    };

    const promote = async (run) => {
        if (promoting) return;
        setPromoting(true);
        try {
            await runsApi.promoteToGolden(run.id, { note });
            toast.success(`Promoted ${run.name || run.id.slice(0, 8)} to golden`);
            setShowPromote(false);
            setNote("");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e, "Promote failed"));
        } finally {
            setPromoting(false);
        }
    };

    const succeededRuns = useMemo(
        () => allRuns.filter((r) => r.status === "succeeded"),
        [allRuns],
    );

    return (
        <div data-testid="golden-runs-page">
            <PageHeader
                title="Golden Runs"
                subtitle="Blessed replay baselines — one click below to promote a succeeded run."
                actions={[
                    <button
                        key="p"
                        onClick={openPromote}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] bg-ux-accent text-black"
                        data-testid="golden-promote-button"
                    >
                        <Trophy className="w-4 h-4" /> Promote a run
                    </button>,
                ]}
            />

            {err && (
                <div className="mb-4 p-3 rounded-md border border-ux-danger/40 bg-ux-danger/10 text-ux-danger text-[13px]">
                    {err}
                </div>
            )}

            <div className="rounded-lg border border-ux-border-1 overflow-hidden">
                <table className="w-full text-[13px]" data-testid="golden-table">
                    <thead className="bg-ux-bg-1 text-ux-text-3 text-[11px] uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-3 py-2">Run</th>
                            <th className="text-left px-3 py-2">Output hash</th>
                            <th className="text-left px-3 py-2">Scenario template</th>
                            <th className="text-left px-3 py-2">Promoted by</th>
                            <th className="text-left px-3 py-2">Promoted at</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={5} className="px-3 py-8 text-center text-ux-text-3">
                                    loading…
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-3 py-8 text-center text-ux-text-3">
                                    No goldens yet.
                                </td>
                            </tr>
                        )}
                        {rows.map((g) => {
                            const rid = g.run_id || g.id;
                            return (
                                <tr
                                    key={g.id || rid}
                                    className="border-t border-ux-border-1 hover:bg-ux-bg-1"
                                    data-testid={`golden-row-${g.id || rid}`}
                                >
                                    <td className="px-3 py-2 font-mono text-ux-text-2">
                                        {rid?.slice(0, 12)}…
                                    </td>
                                    <td className="px-3 py-2 font-mono text-ux-text-3">
                                        {(g.output_hash || "").slice(0, 10)}…
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-1">
                                        {g.scenario_template || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-2">
                                        {g.promoted_by || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-3 font-mono">
                                        {g.promoted_at ? new Date(g.promoted_at).toLocaleString() : "—"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showPromote && (
                <div
                    className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowPromote(false);
                    }}
                >
                    <div
                        className="w-full max-w-2xl bg-ux-bg-1 rounded-lg border border-ux-border-1 flex flex-col max-h-[85vh]"
                        data-testid="golden-promote-modal"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-ux-border-1">
                            <h2 className="text-[15px] font-semibold text-ux-text-1">
                                Promote a run to golden
                            </h2>
                            <button
                                onClick={() => setShowPromote(false)}
                                className="text-ux-text-3 hover:text-ux-text-1"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="px-4 py-3 border-b border-ux-border-1">
                            <label className="text-[11px] uppercase tracking-wider text-ux-text-3">
                                Note (optional)
                            </label>
                            <input
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="why is this one golden?"
                                className="mt-1 w-full px-2 py-1.5 rounded-md bg-ux-bg-2 border border-ux-border-1 text-[13px] text-ux-text-1"
                                data-testid="golden-promote-note"
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            <table className="w-full text-[13px]">
                                <thead className="bg-ux-bg-2 text-ux-text-3 text-[11px] uppercase tracking-wider sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-2">Run</th>
                                        <th className="text-left px-3 py-2">Samples</th>
                                        <th className="text-left px-3 py-2">Output hash</th>
                                        <th className="text-right px-3 py-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {succeededRuns.slice(0, 40).map((r) => (
                                        <tr
                                            key={r.id}
                                            className="border-t border-ux-border-1"
                                            data-testid={`golden-promote-row-${r.id}`}
                                        >
                                            <td className="px-3 py-2 font-mono text-ux-text-2">
                                                {r.name || r.id.slice(0, 12) + "…"}
                                            </td>
                                            <td className="px-3 py-2">{r.samples_planned ?? "—"}</td>
                                            <td className="px-3 py-2 font-mono text-ux-text-3">
                                                {(r.output_hash || "").slice(0, 10)}…
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    onClick={() => promote(r)}
                                                    disabled={promoting}
                                                    className="text-[12px] text-ux-accent hover:underline disabled:opacity-50"
                                                    data-testid={`golden-promote-action-${r.id}`}
                                                >
                                                    promote →
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
